import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import Banner from "@cs/components/src/Banner";
import VariableInput from "./VariableInput";
import InputGroup from "@cs/components/src/InputGroup";
import Table from "@cs/components/src/ThemedTable";
import Button from "@cs/components/src/Button";
import AnimateHeight, {
  AnimateHeightRef,
} from "@cs/components/src/AnimateHeight";
import { ChevronRight, ChevronDown, Trash2 } from "react-feather";
import { splitVariableSections, isVariableSeparator } from "./utils";

type Value = { variables: VariableInstance[]; titles?: TitleInstance[] };

interface EntryControls {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
  onDelete: () => void;
}

interface Props<T extends Value> {
  value: T;
  config?: PluginConfigItem | ScriptConfigItem | ListInputConfig;
  onChange: (value: T) => void;
  noMargin?: boolean;
  /** Collapsed state of the parent subgroup — determines read vs edit display.
   *  When true (collapsed/summary view) fields render read-only summaries.
   *  When false/undefined (expanded/editable) fields render editable inputs.
   *  Defaults to false (always editable for the top-level plugin form). */
  isCollapsed?: boolean;
  /** H1: server-resolved friendly labels keyed by variableConfigId. */
  variableLabels?: Record<string, string>;
  /**
   * When provided, renders this form as a collapsible list entry instead of a
   * standalone InputGroup. The chevron and trash icon render inline on the first
   * field row; remaining rows collapse/expand via AnimateHeight. No outer box.
   */
  entryControls?: EntryControls;
}

const Wrapper = styled.div`
  margin: 0 10px;
`;

const StyledInputGroup = styled(InputGroup)<{ $noMargin?: boolean }>`
  overflow: hidden;
  padding-top: 5px;

  ${(props) =>
    !props.$noMargin
      ? css`
          margin: 10px 10px;
        `
      : css`
          margin: 0;
        `}
`;

// Entry mode — no outer box, controls inline on first row.
const EntryFirstRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0 5px;
`;

const EntryFirstRowContent = styled.div`
  flex: 1;
  min-width: 0;
`;

// Rest rows animate in/out. Padded to align label/value columns with the first
// row (chevron button = 30px, trash button = 30px, both icon-only with no padding).
const EntryRestRowsInner = styled.div`
  overflow: hidden;
  padding-left: 35px;
  padding-right: 35px;
`;

const VariableInputForm = <T extends Value>(props: Props<T>) => {
  const {
    onChange,
    config,
    value: instance,
    noMargin,
    isCollapsed = false,
    variableLabels,
    entryControls,
  } = props;
  const { systemDomainId } = useParams();

  // Collapsed state for each named section (keyed by index:title).
  const [collapsedSections, setCollapsedSections] = useState<
    Record<string, boolean>
  >({});

  // Ref for AnimateHeight in entry mode.
  const entryAnimRef = useRef<AnimateHeightRef>(null);

  const makeRow = (variable: VariableConfig) => {
    const variableId = variable.id ?? variable.key;
    const variableInstance = instance.variables.find(
      ({ variableConfigId, dataType }) =>
        variableConfigId === variableId && dataType === variable.dataType
    );
    return {
      id: variableId,
      element: () => (
        <VariableInput
          variable={variable}
          value={variableInstance?.value}
          systemDomainId={systemDomainId}
          variables={instance.variables}
          config={config}
          isCollapsed={isCollapsed}
          variableLabels={variableLabels}
          onChange={(value) => {
            const obj = {
              variableConfigId: variableId,
              value,
              dataType: variable.dataType,
            };
            if (variableInstance) {
              onChange({
                ...instance,
                variables: instance.variables.map((vi) =>
                  vi.variableConfigId === variableId &&
                  vi.dataType === variable.dataType
                    ? obj
                    : vi
                ),
              });
            } else {
              onChange({
                ...instance,
                variables: [...instance.variables, obj],
              });
            }
          }}
        />
      ),
    };
  };

  // Entry mode: inline chevron + first field + trash; rest rows animate.
  if (entryControls) {
    const { collapsed, onCollapse, onDelete } = entryControls;
    const allVars = (config?.variables ?? []).filter(
      (v) => !isVariableSeparator(v.dataType)
    );
    const firstVar = allVars[0];
    const restVars = allVars.slice(1);

    if (!firstVar) {
      return null;
    }

    const toggleCollapse = () => {
      entryAnimRef.current?.update();
      onCollapse(!collapsed);
    };

    // Render null when collapsed so AnimateHeight starts/ends at height 0,
    // matching the same pattern InputGroup uses for collapsedChildren.
    const restContent = collapsed ? null : (
      <>
        {restVars.map((v) => (
          <React.Fragment key={v.id ?? v.key}>
            {makeRow(v).element()}
          </React.Fragment>
        ))}
      </>
    );

    return (
      <>
        <EntryFirstRow>
          <Button
            icon={collapsed ? ChevronRight : ChevronDown}
            onClick={toggleCollapse}
            size={30}
            type="default"
            borderless
          />
          <EntryFirstRowContent>
            {makeRow(firstVar).element()}
          </EntryFirstRowContent>
          <Button
            icon={Trash2}
            type="danger"
            borderless
            size={30}
            tooltip="Remove entry"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          />
        </EntryFirstRow>
        {restVars.length > 0 && (
          <EntryRestRowsInner>
            <AnimateHeight ref={entryAnimRef}>{restContent}</AnimateHeight>
          </EntryRestRowsInner>
        )}
      </>
    );
  }

  if (!config?.variables.length) {
    return (
      <StyledInputGroup $noMargin={noMargin} noHover>
        <Banner placeholder="Nothing to configure" />
      </StyledInputGroup>
    );
  }

  const sections = splitVariableSections(config.variables);
  const hasSeparators = sections.some((s) => s.title !== undefined);

  if (!hasSeparators) {
    // No separators — render flat in a single InputGroup.
    const section = sections[0];
    return (
      <StyledInputGroup $noMargin={noMargin} noHover>
        <Table
          rowPadding="0"
          rowHeight={34}
          rows={section.variables.map(makeRow)}
        />
      </StyledInputGroup>
    );
  }

  // D1: Sectioned — each named section renders as a top-level SectionInputGroup
  // (no shared outer wrapper). D2: expanded by default.
  return (
    <Wrapper>
      {sections.map((section, index) => {
        if (section.title === undefined) {
          // Leading ungrouped variables — render inline without a group wrapper.
          if (section.variables.length === 0) {
            return null;
          }
          return (
            <React.Fragment key="__ungrouped__">
              <Table
                rowPadding="0"
                rowHeight={34}
                rows={section.variables.map(makeRow)}
              />
            </React.Fragment>
          );
        }

        const sectionKey = `${index}:${section.title}`;
        const isSectionCollapsed = collapsedSections[sectionKey] === true; // D2: default expanded

        return (
          <InputGroup
            key={sectionKey}
            title={section.title}
            collapsible
            collapsed={isSectionCollapsed}
            setCollapsed={(c) =>
              setCollapsedSections((prev) => ({ ...prev, [sectionKey]: c }))
            }
          >
            <Table
              rowPadding="0"
              rowHeight={34}
              rows={section.variables.map(makeRow)}
            />
          </InputGroup>
        );
      })}
    </Wrapper>
  );
};

export default VariableInputForm;
